import { CDTSegmentType, ColumnDef, SegmentAggregates } from 'src/models/segment-cdt/SegmentCdtTypes';
import { TPETableFooterRow } from 'src/components/shared/TPEReactTable';
import { NO_VALUE } from 'src/components/segment-cdt/detail/segmentDetailColumns';
import { formatSegmentType } from 'src/models/segment-cdt/SegmentTableListRow';

/**
 * The two pinned summary rows shown beneath the segment rows: the period's total per column, and the
 * unallocated remainder per column.
 *
 * Both figures are server-computed by `GetSegmentAggregates` and are **never summed client-side** - the grid
 * holds one page at a time, so a client-side sum would silently be a page total presented as a period total.
 *
 * The two rows say different things and are deliberately not combined:
 *  - `aggregatedTotal[columnId]` is the sum of the period's stored segment rows.
 *  - `unallocatedAmount[columnId]` is the GL-pulled figure minus that sum, i.e. the part of the pull that
 *    landed on no row. Non-zero means the pull and the rows do not reconcile, which is why it is surfaced
 *    rather than folded into the total.
 *
 * An **absent** entry is not zero. A column whose values could not be parsed is omitted from the maps
 * entirely, and `unallocatedAmount` is absent by design for MANUAL / DERIVED_PCT columns, which have no GL
 * figure to reconcile against. Rendering a missing key as `0` would assert a reconciliation that was never
 * computed, so it renders as the same dash the data cells use for an absent value.
 */
export function buildAggregateFooterRows(
    aggregates: SegmentAggregates | undefined,
    columns: ColumnDef[],
    fixedColumnCount: number,
    segmentType?: CDTSegmentType,
): TPETableFooterRow[] {
    if (!aggregates) {
        return [];
    }
    const valueColumns = columns.filter((c) => !!c.columnId);
    if (!valueColumns.length) {
        return [];
    }

    const totals = aggregates.aggregatedTotal ?? {};
    const unallocated = aggregates.unallocatedAmount ?? {};

    // The label spans the identity columns so the figures line up under their own value columns, matching
    // where each column's cells sit in the data rows above.
    const totalLabel = aggregates.rowCount != null
        ? `Total (${aggregates.rowCount.toLocaleString()} ${segmentGrainLabel(segmentType, aggregates.rowCount)})`
        : 'Total';

    const rows: TPETableFooterRow[] = [
        {
            className: 'segmentTotalRow',
            cells: [
                { content: totalLabel, colSpan: fixedColumnCount, className: 'segmentFooterLabel' },
                ...valueColumns.map((c) => ({
                    content: formatAmount(totals[c.columnId as string]),
                    className: 'segmentFooterValue',
                })),
            ],
        },
    ];

    // Only shown when the server computed at least one: for a table of MANUAL / DERIVED_PCT columns there is
    // no GL figure anywhere, and an all-blank row would just be noise.
    const hasAnyUnallocated = valueColumns.some((c) => unallocated[c.columnId as string] != null);
    if (hasAnyUnallocated) {
        rows.push({
            className: 'segmentUnallocatedRow',
            cells: [
                {
                    content: 'Unallocated (GL Aggregate - Segment Sum)',
                    colSpan: fixedColumnCount,
                    className: 'segmentFooterLabel',
                },
                ...valueColumns.map((c) => ({
                    content: formatAmount(unallocated[c.columnId as string]),
                    className: 'segmentFooterValue',
                })),
            ],
        });
    }

    return rows;
}

/**
 * Plural of each segment grain, for the row-count label.
 *
 * Spelled out rather than derived by appending `s`, because that rule is wrong for the one grain whose label
 * is not a bare noun: `ENTITY` surfaces as "Legal Entity" (see `SEGMENT_TYPE_LABELS`), which would pluralise
 * to "legal entitys". The singular always comes from `formatSegmentType`, so only the plural needs a table
 * and the two cannot drift.
 */
const SEGMENT_GRAIN_PLURALS: Record<string, string> = {
    COST_CENTER: 'cost centers',
    ENTITY: 'legal entities',
    LOCATION: 'locations',
    PRODUCT_LINE: 'product lines',
    CHANNEL: 'channels',
    PROJECT: 'projects',
};

/**
 * The grain the row count counts, so "Total (4,441 cost centers)" reads as the domain does. Falls back to
 * the neutral "rows" when the table's segment type has not loaded yet, and to a bare `s` for a segment type
 * added to the contract but not yet to the plural table - wrong-but-readable beats crashing on a label the
 * UI has never seen.
 */
function segmentGrainLabel(segmentType?: CDTSegmentType, count?: number): string {
    if (!segmentType) {
        return 'rows';
    }
    const singular = formatSegmentType(segmentType).toLowerCase();
    if (count === 1) {
        return singular;
    }
    return SEGMENT_GRAIN_PLURALS[segmentType as unknown as string] ?? `${singular}s`;
}

/**
 * Thousands-separated, or {@link NO_VALUE} when the server returned no figure for the column.
 *
 * Absent is still never rendered as `0` — that is the load-bearing rule, and a total the server did not
 * compute must not be stated as zero. It renders as the same dash the data cells above use rather than as
 * blank, so one column does not carry two spellings of "nothing here": these cells sit directly under the
 * value columns, and a reader cannot tell an empty summary cell from an empty data cell.
 */
function formatAmount(value?: number): string {
    return value == null ? NO_VALUE : value.toLocaleString();
}
